"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col", className)}
      {...props}
    />
  )
}

interface TabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  variant?: "default" | "underline";
}

function TabsList({
  className,
  variant = "default",
  ...props
}: TabsListProps) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "inline-flex items-center",
        variant === "default" && "h-9 w-fit justify-center rounded-lg bg-muted p-[3px] text-muted-foreground",
        variant === "underline" && "h-[50px] w-full border-b border-slate-200",
        className
      )}
      {...props}
    />
  )
}

// Context for the elastic underline indicator
interface ElasticTabsContextValue {
  registerTab: (value: string, element: HTMLButtonElement | null) => void;
  setHoveredTab: (value: string | null) => void;
  activeTab: string | null;
  hoveredTab: string | null;
}

const ElasticTabsContext = React.createContext<ElasticTabsContextValue | null>(null);

interface ElasticTabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  activeValue?: string;
}

function ElasticTabsList({
  className,
  children,
  activeValue,
  ...props
}: ElasticTabsListProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tabRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const [hoveredTab, setHoveredTab] = React.useState<string | null>(null);
  const [indicatorStyle, setIndicatorStyle] = React.useState<React.CSSProperties>({
    left: 0,
    width: 0,
    opacity: 0,
  });

  const registerTab = React.useCallback((value: string, element: HTMLButtonElement | null) => {
    if (element) {
      tabRefs.current.set(value, element);
    } else {
      tabRefs.current.delete(value);
    }
  }, []);

  // Calculate indicator position based on hovered or active tab
  const updateIndicatorPosition = React.useCallback(() => {
    const targetValue = hoveredTab || activeValue;
    if (!targetValue || !containerRef.current) return;

    const targetElement = tabRefs.current.get(targetValue);
    if (!targetElement) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();

    setIndicatorStyle({
      left: targetRect.left - containerRect.left,
      width: targetRect.width,
      opacity: 1,
    });
  }, [hoveredTab, activeValue]);

  // Update indicator when hover or active changes
  React.useEffect(() => {
    updateIndicatorPosition();
  }, [updateIndicatorPosition]);

  // Also update on mount and resize
  React.useEffect(() => {
    const handleResize = () => updateIndicatorPosition();
    window.addEventListener("resize", handleResize);
    
    // Initial position update after a short delay to ensure refs are set
    const timer = setTimeout(updateIndicatorPosition, 50);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, [updateIndicatorPosition]);

  const contextValue = React.useMemo<ElasticTabsContextValue>(
    () => ({
      registerTab,
      setHoveredTab,
      activeTab: activeValue || null,
      hoveredTab,
    }),
    [registerTab, activeValue, hoveredTab]
  );

  return (
    <ElasticTabsContext.Provider value={contextValue}>
      <div ref={containerRef} className="relative">
        <TabsPrimitive.List
          data-slot="tabs-list"
          className={cn(
            "inline-flex items-center h-[50px] w-full border-b border-slate-200",
            className
          )}
          {...props}
        >
          {children}
        </TabsPrimitive.List>
        
        {/* Elastic indicator - positioned at the bottom border */}
        <div
          className="absolute bottom-0 h-[2px] bg-text-primary transition-all duration-300 ease-out pointer-events-none z-10"
          style={indicatorStyle}
        />
      </div>
    </ElasticTabsContext.Provider>
  );
}

interface TabsTriggerProps extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
  variant?: "default" | "underline";
}

function TabsTrigger({
  className,
  variant = "default",
  ...props
}: TabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm disabled:pointer-events-none disabled:opacity-50",
        variant === "default" && [
          "h-[calc(100%-1px)] flex-1 rounded-md border border-transparent px-2 py-1 font-semibold transition-colors",
          "data-[state=active]:bg-background data-[state=active]:shadow-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:outline-1",
          "dark:data-[state=active]:text-foreground dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30",
          "text-foreground dark:text-muted-foreground",
        ],
        variant === "underline" && [
          "h-full px-4 py-3.5 border-b",
          "text-slate-400 border-slate-200 font-medium",
          "data-[state=active]:text-text-primary data-[state=active]:border-text-primary data-[state=active]:font-semibold",
          "hover:text-slate-600",
        ],
        className
      )}
      {...props}
    />
  )
}

// Special trigger for elastic tabs that registers itself and handles hover
type ElasticTabsTriggerProps = React.ComponentProps<typeof TabsPrimitive.Trigger>;

function ElasticTabsTrigger({
  className,
  value,
  children,
  ...props
}: ElasticTabsTriggerProps) {
  const context = React.useContext(ElasticTabsContext);
  const ref = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (context && value) {
      context.registerTab(value, ref.current);
      return () => context.registerTab(value, null);
    }
  }, [context, value]);

  const handleMouseEnter = () => {
    if (context && value) {
      context.setHoveredTab(value);
    }
  };

  const handleMouseLeave = () => {
    if (context) {
      context.setHoveredTab(null);
    }
  };

  const isActive = context?.activeTab === value;
  const isHovered = context?.hoveredTab === value;

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-slot="tabs-trigger"
      value={value}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm disabled:pointer-events-none disabled:opacity-50",
        "h-full px-4 py-3.5 border-b border-transparent",
        "text-slate-400 font-medium transition-colors",
        (isActive || isHovered) && "text-text-primary",
        isActive && "font-semibold",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, ElasticTabsList, ElasticTabsTrigger }
